import type { ChangeEvent, FormEvent, KeyboardEvent, MouseEvent } from 'react';
import { refusalDestinationOpen } from '@featit/shared/refusal-destination';
import { gateReason, type WorkAreaGateType } from '@/utils/workAreaPermissionGate';
import { busyControlProps } from '@/utils/busyControl';

/**
 * How a control gated by `workAreaGate` looks and behaves in each state (I-0676, concept-access-
 * control §14.2a, AC-P5 and AC-P6).
 *
 * ⛔ **Pending is not refused.** A disabled, dimmed control says "you may not", which is false for
 * the ~140 ms a work area's access takes to arrive, and alarming on a slow network. So a pending
 * control keeps its **permitted appearance** — no `disabled`, so none of the dimming or the
 * not-allowed cursor the `disabled:` styles draw — and is inert all the same: `aria-disabled`,
 * `aria-busy`, and a click that does nothing. It says it is checking as a refusal says why
 * (I-1104 AC-DH44): "Checking access to this work area…" is its `title` and its `aria-describedby`,
 * so no control is ever held with no reason, however long its read takes.
 *
 * ⭐ **Refused is shown, disabled, and says why only in its tooltip** (F-0324, Jay 2026-09-30:
 * *"no edit but read should still keep the elements visible"*, *"dont clutter the ui with
 * explanations why something is disabled"*). A refused control is `aria-disabled`, dimmed by
 * `[data-gate='refused']` in `index.css`, stays in the tab order and ignores activation; its reason
 * is its `title` — the hover tooltip and what a tap shows (`RefusedTouchHint`) — and its
 * `aria-describedby`, the node `RefusalReasons` draws, which a screen reader reads when the control
 * takes focus (AC-DH34: a title alone may not be read). It is **not** `disabled`: a disabled button takes no
 * focus and, under Button's `disabled:pointer-events-none`, no hover, so its reason reached nobody.
 * No screen writes the reason as text beside the control.
 *
 * ⛔ Every control a missing permission governs goes through here. `disableNotHide.guard.test.ts`
 * names a control that is hidden instead.
 */

/**
 * How long access may stay unknown before a pending control shows that it is still checking (AC-P6).
 * The indicator itself is drawn by `index.css` on the attributes below — every gated control's, so no
 * screen draws its own (F-0533 KD-5); `pendingIndicator.guard.test.ts` holds that rule's delay to this.
 */
export const PENDING_INDICATOR_DELAY_MS = 400;

/**
 * F-0324 AC-DH34: the id of the node that describes a refusal, derived from the reason's text (FNV-1a,
 * 32 bits), so every control refused for the same reason names the same node and a re-render never
 * changes it. `RefusalReasons`, at the app's root, draws the node for each id a control in the page
 * names; a screen reader reads it as the control's description.
 */
export const REFUSAL_REASON_ID_PREFIX = 'refusal-reason-';
export function refusalReasonId(reason: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < reason.length; index += 1) {
    hash ^= reason.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${REFUSAL_REASON_ID_PREFIX}${hash.toString(16).padStart(8, '0')}`;
}

/**
 * The `aria-describedby` a refused control carries: the control's own descriptions (`describedBy`, a
 * hint or a problem it already names), then its reason's node. Passed in, never set beside the
 * spread, so neither replaces the other (`disableNotHide.guard.test.ts` rule 7).
 */
function describedByWith(reason: string, describedBy: string | undefined): string {
  return [describedBy, refusalReasonId(reason)].filter(Boolean).join(' ');
}

type GateControlPropsType = {
  disabled: boolean;
  title: string | undefined;
  'aria-describedby'?: string;
  'aria-disabled'?: true;
  'aria-busy'?: true;
  'data-busy'?: true;
  'data-gate'?: 'refused';
  'data-gate-destination'?: 'true';
  'data-held'?: true;
  onClickCapture?: (event: MouseEvent) => void;
};

/** Activation ignored — by click, and by Enter or Space, which a button or link turns into a click. */
function ignoreActivation(event: MouseEvent): void {
  event.preventDefault();
  event.stopPropagation();
}

/**
 * Pressing a control whose access could not be read asks again (I-1702, KD-17): the press itself
 * still does nothing else, so the control's own action never runs on an unread answer.
 */
function retryOnActivation(retry: () => void): (event: MouseEvent) => void {
  return (event) => {
    ignoreActivation(event);
    retry();
  };
}

/**
 * I-1914: pressing a control refused with a destination the viewer may open shows its reason with a
 * link there (`explain`, from `useRefusalDestination`); the press itself still does nothing else.
 */
function explainOnActivation(explain: () => void): (event: MouseEvent) => void {
  return (event) => {
    ignoreActivation(event);
    explain();
  };
}

/**
 * The attributes a gated control spreads: `disabled` only when refused (or when `otherwiseDisabled`
 * says so for its own reasons), and inert-but-undimmed while pending. A refusal that carries a
 * `retry` (an unread answer, `heldGate`) asks again when pressed; one that carries a destination the
 * viewer may open, and an `explain`, opens its reason with a link to that place (I-1914).
 *
 * I-2317: `busy` is the control's own work in progress. A permitted control keeps its focus while
 * it runs (`busyControlProps`), where `otherwiseDisabled: saving` would have disabled it natively;
 * a pending or refused gate already refuses the press and keeps its own look.
 */
export function gateControlProps(
  gate: WorkAreaGateType,
  {
    otherwiseDisabled = false,
    title,
    describedBy,
    explain,
    busy = false,
    heldBecause,
  }: {
    otherwiseDisabled?: boolean;
    /**
     * I-2590: why `otherwiseDisabled` holds the control, when the person needs to know. With it the
     * held control stays focusable, `aria-disabled`, with this reason as its title and description,
     * and ignores activation; without it a control disabled for its own reasons (nothing to save)
     * stays natively disabled, as `busyControl.ts` says.
     */
    heldBecause?: string;
    title?: string;
    describedBy?: string;
    explain?: () => void;
    busy?: boolean;
  } = {},
): GateControlPropsType {
  const own = describedBy ? { 'aria-describedby': describedBy } : {};
  if (gate.state === 'pending') {
    const checking = gateReason(gate) ?? '';
    return {
      disabled: otherwiseDisabled,
      title: checking,
      'aria-describedby': describedByWith(checking, describedBy),
      'aria-disabled': true,
      'aria-busy': true,
      onClickCapture: ignoreActivation,
    };
  }
  if (gate.state === 'refused') {
    // Never `disabled`, even for its own reasons: activation is ignored anyway, and a disabled
    // control would lose the focus and the hover that carry the reason. The `title` is for a
    // pointer's hover and the touch hint; the description is what a screen reader reads (AC-DH34).
    const explainer =
      !gate.retry && explain && refusalDestinationOpen(gate.destination) ? explain : null;
    return {
      disabled: false,
      title: gate.reason,
      'aria-describedby': describedByWith(gate.reason, describedBy),
      'aria-disabled': true,
      'data-gate': 'refused',
      // The popover shows the reason on a tap, so the touch hint stays away (`RefusedTouchHint`).
      ...(explainer ? { 'data-gate-destination': 'true' as const } : {}),
      onClickCapture: gate.retry
        ? retryOnActivation(gate.retry)
        : explainer
          ? explainOnActivation(explainer)
          : ignoreActivation,
    };
  }
  if (otherwiseDisabled && heldBecause) {
    // I-2590: held for a reason the person needs (a conflict unanswered, nothing chosen yet), the
    // control keeps the refused form's focus and description, so a keyboard and a screen reader
    // reach the reason on the control itself; only the dim's mark differs (`data-held`).
    return {
      disabled: false,
      title: heldBecause,
      'aria-describedby': describedByWith(heldBecause, describedBy),
      'aria-disabled': true,
      'data-held': true,
      onClickCapture: ignoreActivation,
    };
  }
  return { title, ...own, ...busyControlProps(busy, { disabled: otherwiseDisabled }) };
}

/**
 * I-2595: the gate of a field no permission governs, for a field held only for a reason of its own
 * (`gateFieldProps(NOT_GATED, kind, { heldBecause })`).
 */
export const NOT_GATED: WorkAreaGateType = { state: 'permitted', basis: 'granted' };

/**
 * I-2594: the same held form for a control no work-area permission governs (one conversation open,
 * nothing to reconnect, a native shell missing): held with `heldBecause`, it stays focusable and says
 * why; held without one, it stays natively disabled, as `gateControlProps` does once permitted.
 *
 * `reasonShownIn` is the id of a node on the screen that already says `heldBecause`: the held control
 * is described by it instead of by the root's node, so a screen reader reads the reason once.
 */
export function heldControlProps({
  reasonShownIn,
  ...options
}: Omit<NonNullable<Parameters<typeof gateControlProps>[1]>, 'explain'> & {
  reasonShownIn?: string;
} = {}): GateControlPropsType {
  const props = gateControlProps(NOT_GATED, options);
  if (!props['data-held'] || !reasonShownIn) return props;
  return {
    ...props,
    'aria-describedby': [options.describedBy, reasonShownIn].filter(Boolean).join(' '),
  };
}

type GateFieldPropsType<E> = {
  title: string | undefined;
  'aria-describedby'?: string;
  readOnly?: true;
  'aria-disabled'?: true;
  'aria-busy'?: true;
  'data-gate'?: 'refused';
  'data-held'?: true;
  onChange?: (event: E) => void;
  onClickCapture?: (event: MouseEvent) => void;
  onMouseDown?: (event: MouseEvent) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
};

/** A change a blocked field takes: nothing is written. */
function ignoreChange(): void {}

/** A select that must not open: pressing it, or any key but Tab, does nothing. */
function ignorePress(event: MouseEvent): void {
  event.preventDefault();
}
function ignoreKeysButTab(event: KeyboardEvent): void {
  if (event.key !== 'Tab') event.preventDefault();
}

/** The keys a radio group moves its selection by, and so checks another radio with (AC-DH41). */
const SELECTION_KEYS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** A toggle that must not flip: a radio group's arrows, which check the next radio, do nothing. */
function ignoreSelectionKeys(event: KeyboardEvent): void {
  if (SELECTION_KEYS.has(event.key)) event.preventDefault();
}

/**
 * The same refused look for a **field** — a text input, a textarea, a select, a checkbox (F-0324,
 * decision 3 of the second pass). Its value stays readable and selectable, and it stays in the tab
 * order with the reason as its tooltip and accessible description; it takes no change:
 *
 * - `text` (input, textarea): `readOnly`, so the value can be read, selected and copied, not typed;
 * - `select`: it does not open — a press and every key but Tab are ignored;
 * - `toggle` (checkbox, radio): the click that would flip it is ignored (a pointer and Space alike),
 *   and so are a radio group's arrow keys, which would check the next radio (AC-DH41).
 *
 * I-2595 (AC-DH40, DH41): `heldBecause` is a field held for a reason of its own, not a permission
 * (nothing chosen yet, the view draws every level), as `gateControlProps`' is for a button: the
 * same form as refused, its reason the `title` and the description, marked `data-held`. Pass it only
 * while the field is held; a field disabled with no reason the person needs stays natively disabled.
 *
 * ⚠️ **The field's `onChange` goes through here** (`{ onChange }`), and comes back as a no-op while
 * the gate blocks, so a way in the list above did not foresee — a phone's native picker — still
 * writes nothing. A capture-phase stop cannot do it: React runs a field's `onChangeCapture` and
 * `onChange` in one pass on one element, and only checks for a stopped event between elements.
 *
 * ⛔ Not `disabled`: a disabled field takes no focus and no pointer, so its reason reached nobody —
 * the same reason as for a button. Permitted returns the caller's own `title` and `onChange`; the
 * field keeps its own reasons to be disabled (a write in flight) through its own `disabled`.
 */
export function gateFieldProps<E = ChangeEvent<HTMLElement>>(
  gate: WorkAreaGateType,
  kind: 'text' | 'select' | 'toggle',
  {
    title,
    onChange,
    describedBy,
    heldBecause,
  }: {
    title?: string;
    onChange?: (event: E) => void;
    describedBy?: string;
    heldBecause?: string;
  } = {},
): GateFieldPropsType<E> {
  const own = describedBy ? { 'aria-describedby': describedBy } : {};
  if (gate.state === 'permitted' && !heldBecause)
    return onChange ? { title, ...own, onChange } : { title, ...own };
  const blocked: GateFieldPropsType<E> =
    gate.state === 'permitted'
      ? {
          title: heldBecause,
          'aria-describedby': describedByWith(heldBecause ?? '', describedBy),
          'aria-disabled': true,
          onChange: ignoreChange,
          'data-held': true,
        }
      : {
          title: gateReason(gate) ?? title,
          // I-1104 AC-DH44: pending says it is checking, as refused says why.
          'aria-describedby': describedByWith(gateReason(gate) ?? '', describedBy),
          'aria-disabled': true,
          onChange: ignoreChange,
          ...(gate.state === 'pending'
            ? { 'aria-busy': true as const }
            : { 'data-gate': 'refused' as const }),
        };
  if (kind === 'text') return { ...blocked, readOnly: true };
  if (kind === 'select')
    return { ...blocked, onMouseDown: ignorePress, onKeyDown: ignoreKeysButTab };
  return { ...blocked, onClickCapture: ignoreActivation, onKeyDown: ignoreSelectionKeys };
}

type GateFieldsetPropsType = {
  title: string | undefined;
  'aria-describedby'?: string;
  'aria-disabled'?: true;
  'aria-busy'?: true;
  'data-gate'?: 'refused';
  'data-held'?: true;
  onChangeCapture?: (event: ChangeEvent<HTMLElement>) => void;
  onBeforeInputCapture?: (event: FormEvent<HTMLElement>) => void;
  onClickCapture?: (event: MouseEvent) => void;
  onMouseDownCapture?: (event: MouseEvent) => void;
  onKeyDownCapture?: (event: KeyboardEvent) => void;
};

/** A select or a radio inside a blocked group: the keys that would open it or move its choice. */
function ignoreGroupKeys(event: KeyboardEvent): void {
  const target = event.target as HTMLElement;
  if (target instanceof HTMLSelectElement) ignoreKeysButTab(event);
  else if (target instanceof HTMLInputElement && target.type === 'radio')
    ignoreSelectionKeys(event);
}
function ignoreGroupPress(event: MouseEvent): void {
  if (event.target instanceof HTMLSelectElement) event.preventDefault();
}

/**
 * I-2595 (AC-DH42): a **fieldset** refused for a permission, or held for a reason of its own
 * (`heldBecause`), holds its whole group without leaving the tab order. ⛔ Never natively `disabled`:
 * that disables every field inside, so none takes focus and the reason reaches nobody. Instead the
 * group is `aria-disabled` and described by its reason (read with its legend on entering it), and
 * it takes nothing on its way down: a change is stopped before the field's own `onChange` (a
 * controlled field keeps its value), typing is cancelled before it reaches the field (an uncontrolled
 * field shows nothing either), a press is refused (a checkbox, a radio, a button), and a select does
 * not open nor a radio group move. Every field inside stays focusable and readable. Permitted and not
 * held, it is a plain group.
 */
export function gateFieldsetProps(
  gate: WorkAreaGateType,
  {
    title,
    describedBy,
    heldBecause,
  }: { title?: string; describedBy?: string; heldBecause?: string } = {},
): GateFieldsetPropsType {
  const own = describedBy ? { 'aria-describedby': describedBy } : {};
  if (gate.state === 'permitted' && !heldBecause) return { title, ...own };
  const reason = gate.state === 'permitted' ? heldBecause : gateReason(gate);
  return {
    title: reason ?? title,
    // I-1104 AC-DH44: pending says it is checking, as refused says why.
    ...(!reason ? own : { 'aria-describedby': describedByWith(reason, describedBy) }),
    'aria-disabled': true,
    ...(gate.state === 'pending'
      ? { 'aria-busy': true as const }
      : gate.state === 'permitted'
        ? { 'data-held': true as const }
        : { 'data-gate': 'refused' as const }),
    onChangeCapture: (event) => event.stopPropagation(),
    onBeforeInputCapture: (event) => event.preventDefault(),
    onClickCapture: ignoreActivation,
    onMouseDownCapture: ignoreGroupPress,
    onKeyDownCapture: ignoreGroupKeys,
  };
}
